import React, { useState } from 'react';
import MarketingNav from '../components/layout/MarketingNav';
import { Button, Card, Badge } from '../components/ui';
import { BookOpen, Heart, TrendingUp, ChevronRight } from 'lucide-react';

const ClientPreview = () => {
  const [activeView, setActiveView] = useState('dashboard');

  // Mock user data
  const mockUser = {
    firstName: 'Sarah',
    lastName: 'Mitchell',
    currentDay: 45,
    currentPhase: 2,
    enrollmentDate: new Date(Date.now() - 45 * 24 * 60 * 60 * 1000),
    stats: {
      journalEntries: 38,
      moodAverage: 7.2,
      completionRate: 92
    }
  };

  // Mock daily content
  const mockDailyContent = {
    mind: {
      title: 'Mindful Awareness',
      content: 'Today\'s practice focuses on observing thoughts without judgment. Practice the "thought observation" technique for 10 minutes.',
      reflection: 'What patterns do you notice in your thoughts?'
    },
    move: {
      title: 'Gentle Movement',
      content: 'A 20-minute guided walk focusing on grounding techniques. Feel each step, notice your surroundings.',
      reflection: 'How did movement affect your mood today?'
    },
    journal: {
      title: 'Daily Reflection',
      content: 'Process today\'s experiences and insights. No judgment, just honest reflection.',
      reflection: 'What did you learn about yourself today?'
    }
  };

  // Mock journal entries
  const mockJournalEntries = [
    {
      day: 45,
      date: new Date(),
      mood: 8,
      summary: 'Felt more grounded today. The mindfulness practice really helped during a stressful meeting.'
    },
    {
      day: 44,
      date: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000),
      mood: 6,
      summary: 'Struggled this morning but movement helped. Progress, not perfection.'
    },
    {
      day: 43,
      date: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000),
      mood: 7,
      summary: 'Connected with support group today. Reminded me I\'m not alone in this.'
    }
  ];

  // Mock progress data
  const mockProgressData = {
    thisWeek: {
      completed: 6,
      total: 7,
      percentage: 86
    },
    thisMonth: {
      completed: 28,
      total: 30,
      percentage: 93
    },
    allTime: {
      completed: 38,
      total: 45,
      percentage: 84
    },
    moodTrend: [6.2, 6.5, 7.1, 6.8, 7.2, 7.0, 7.2]
  };

  return (
    <>
      <MarketingNav transparent={false} />
      <main className="mx-auto max-w-6xl px-6 py-10">
        {/* Header */}
        <div className="mb-8">
          <Badge variant="primary" size="sm">Client Preview</Badge>
          <h1 className="mt-3 text-4xl font-display font-semibold text-neutral-900">
            Client Experience
          </h1>
          <p className="mt-2 text-lg text-neutral-600">
            See what clients experience when using MHDF Momentum's three-pillar approach
          </p>
        </div>

        {/* Tab Navigation */}
        <div className="mb-6 flex gap-2 border-b border-neutral-200 overflow-x-auto">
          {[
            { id: 'dashboard', label: 'Dashboard', icon: '📊' },
            { id: 'daily', label: 'Daily Content', icon: '📝' },
            { id: 'journal', label: 'Journal', icon: '📖' },
            { id: 'progress', label: 'Progress', icon: '📈' }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveView(tab.id)}
              className={`px-4 py-3 font-medium whitespace-nowrap ${
                activeView === tab.id
                  ? 'border-b-2 border-primary-600 text-primary-600'
                  : 'text-neutral-600 hover:text-neutral-900'
              }`}
            >
              {tab.icon} {tab.label}
            </button>
          ))}
        </div>

        {/* Dashboard View */}
        {activeView === 'dashboard' && (
          <div className="space-y-6">
            {/* Welcome Section */}
            <Card>
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-semibold text-neutral-900">
                    Welcome back, {mockUser.firstName}!
                  </h2>
                  <p className="mt-1 text-neutral-600">
                    Day {mockUser.currentDay} of your mental wellness journey • Phase {mockUser.currentPhase}
                  </p>
                </div>
                <div className="text-right">
                  <div className="text-4xl font-bold text-primary-600">{mockUser.stats.completionRate}%</div>
                  <p className="text-sm text-neutral-600">Completion Rate</p>
                </div>
              </div>
            </Card>

            {/* Stats Cards */}
            <div className="grid gap-4 md:grid-cols-3">
              <Card>
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm text-neutral-600">Journal Entries</p>
                    <p className="mt-1 text-3xl font-bold text-neutral-900">{mockUser.stats.journalEntries}</p>
                  </div>
                  <BookOpen className="h-8 w-8 text-blue-500" />
                </div>
              </Card>

              <Card>
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm text-neutral-600">Mood Average</p>
                    <p className="mt-1 text-3xl font-bold text-neutral-900">{mockUser.stats.moodAverage}/10</p>
                  </div>
                  <Heart className="h-8 w-8 text-rose-500" />
                </div>
              </Card>

              <Card>
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm text-neutral-600">Login Days</p>
                    <p className="mt-1 text-3xl font-bold text-neutral-900">{mockUser.currentDay}</p>
                  </div>
                  <TrendingUp className="h-8 w-8 text-emerald-500" />
                </div>
              </Card>
            </div>

            {/* Three Pillars Activity */}
            <Card>
              <h3 className="text-lg font-semibold text-neutral-900 mb-4">Complete your three pillars</h3>
              <p className="text-sm text-neutral-600 mb-4">Today's activities (Day {mockUser.currentDay})</p>
              
              <div className="grid gap-4 md:grid-cols-3">
                {/* Mind Pillar */}
                <div className="rounded-lg border-2 border-neutral-200 p-4 hover:border-primary-600 transition-colors">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center">
                      <span className="text-lg">🧠</span>
                    </div>
                    <h4 className="font-semibold text-neutral-900">Mind</h4>
                  </div>
                  <p className="text-sm text-neutral-600 mb-3">{mockDailyContent.mind.title}</p>
                  <p className="text-xs text-neutral-500 mb-3">{mockDailyContent.mind.content}</p>
                  <div className="flex items-center justify-between">
                    <Badge variant="neutral" size="sm">10 min</Badge>
                    <Button size="sm" variant="ghost">Start →</Button>
                  </div>
                </div>

                {/* Move Pillar */}
                <div className="rounded-lg border-2 border-neutral-200 p-4 hover:border-emerald-600 transition-colors bg-emerald-50/30">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="h-8 w-8 rounded-full bg-emerald-100 flex items-center justify-center">
                      <span className="text-lg">🚶</span>
                    </div>
                    <h4 className="font-semibold text-neutral-900">Move</h4>
                    <Badge variant="success" size="xs">Completed</Badge>
                  </div>
                  <p className="text-sm text-neutral-600 mb-3">{mockDailyContent.move.title}</p>
                  <p className="text-xs text-neutral-500 mb-3">{mockDailyContent.move.content}</p>
                  <div className="flex items-center justify-between">
                    <Badge variant="neutral" size="sm">20 min</Badge>
                    <Button size="sm" variant="ghost">View →</Button>
                  </div>
                </div>

                {/* Journal Pillar */}
                <div className="rounded-lg border-2 border-neutral-200 p-4 hover:border-rose-600 transition-colors">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="h-8 w-8 rounded-full bg-rose-100 flex items-center justify-center">
                      <span className="text-lg">📝</span>
                    </div>
                    <h4 className="font-semibold text-neutral-900">Journal</h4>
                  </div>
                  <p className="text-sm text-neutral-600 mb-3">{mockDailyContent.journal.title}</p>
                  <p className="text-xs text-neutral-500 mb-3">{mockDailyContent.journal.content}</p>
                  <div className="flex items-center justify-between">
                    <Badge variant="neutral" size="sm">15 min</Badge>
                    <Button size="sm" variant="ghost">Reflect →</Button>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* Daily Content View */}
        {activeView === 'daily' && (
          <div className="space-y-6">
            <Card>
              <div className="mb-6">
                <Badge variant="primary" size="sm">Day {mockUser.currentDay}</Badge>
                <h2 className="mt-3 text-2xl font-semibold text-neutral-900">Today's Three Pillars</h2>
                <p className="mt-1 text-neutral-600">Engage with all three areas of mental wellness</p>
              </div>

              {/* Mind Section */}
              <div className="border-t border-neutral-200 pt-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center text-lg">🧠</div>
                  <div>
                    <h3 className="font-semibold text-neutral-900">{mockDailyContent.mind.title}</h3>
                    <p className="text-sm text-neutral-600">Mindfulness & Self-Awareness</p>
                  </div>
                </div>
                <p className="text-neutral-700 mb-4">{mockDailyContent.mind.content}</p>
                <div className="bg-neutral-50 rounded-lg p-4 mb-4">
                  <p className="font-medium text-neutral-700 mb-2">Today's Reflection:</p>
                  <p className="text-neutral-600 italic">"{mockDailyContent.mind.reflection}"</p>
                </div>
                <Button>Complete Mind Activity</Button>
              </div>

              {/* Move Section */}
              <div className="border-t border-neutral-200 pt-6 mt-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="h-10 w-10 rounded-full bg-emerald-100 flex items-center justify-center text-lg">🚶</div>
                  <div>
                    <h3 className="font-semibold text-neutral-900">{mockDailyContent.move.title}</h3>
                    <p className="text-sm text-neutral-600">Physical Activity & Wellness</p>
                  </div>
                </div>
                <p className="text-neutral-700 mb-4">{mockDailyContent.move.content}</p>
                <div className="bg-emerald-50 rounded-lg p-4 mb-4">
                  <p className="font-medium text-neutral-700 mb-2">Today's Reflection:</p>
                  <p className="text-neutral-600 italic">"{mockDailyContent.move.reflection}"</p>
                </div>
                <Button variant="secondary">Review Movement Activity</Button>
              </div>

              {/* Journal Section */}
              <div className="border-t border-neutral-200 pt-6 mt-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="h-10 w-10 rounded-full bg-rose-100 flex items-center justify-center text-lg">📝</div>
                  <div>
                    <h3 className="font-semibold text-neutral-900">{mockDailyContent.journal.title}</h3>
                    <p className="text-sm text-neutral-600">Emotional Processing & Growth</p>
                  </div>
                </div>
                <p className="text-neutral-700 mb-4">{mockDailyContent.journal.content}</p>
                <div className="bg-rose-50 rounded-lg p-4 mb-4">
                  <p className="font-medium text-neutral-700 mb-2">Today's Reflection:</p>
                  <p className="text-neutral-600 italic">"{mockDailyContent.journal.reflection}"</p>
                </div>
                <Button variant="secondary">Write in Journal</Button>
              </div>
            </Card>
          </div>
        )}

        {/* Journal View */}
        {activeView === 'journal' && (
          <div className="space-y-6">
            <Card>
              <h2 className="text-2xl font-semibold text-neutral-900 mb-2">Your Journal</h2>
              <p className="text-neutral-600 mb-6">
                {mockUser.stats.journalEntries} entries recorded • Track your thoughts and progress
              </p>

              <div className="space-y-4">
                {mockJournalEntries.map((entry, idx) => (
                  <div
                    key={idx}
                    className="rounded-lg border border-neutral-200 p-4 hover:border-primary-600 hover:bg-primary-50/30 transition-all cursor-pointer"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <p className="font-semibold text-neutral-900">Day {entry.day}</p>
                        <p className="text-sm text-neutral-600">{entry.date.toLocaleDateString()}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Heart className="h-5 w-5 text-rose-500 fill-rose-500" />
                        <span className="font-semibold text-neutral-700">{entry.mood}/10</span>
                      </div>
                    </div>
                    <p className="text-neutral-700">{entry.summary}</p>
                    <div className="mt-3 text-sm text-primary-600 flex items-center gap-1">
                      View full entry <ChevronRight className="h-4 w-4" />
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        )}

        {/* Progress View */}
        {activeView === 'progress' && (
          <div className="space-y-6">
            <Card>
              <h2 className="text-2xl font-semibold text-neutral-900 mb-2">Your Progress</h2>
              <p className="text-neutral-600 mb-6">Track your mental wellness journey with detailed metrics</p>

              {/* Completion Stats */}
              <div className="grid gap-4 md:grid-cols-3 mb-8">
                <div className="rounded-lg bg-neutral-50 p-4">
                  <p className="text-sm text-neutral-600 mb-2">This Week</p>
                  <div className="flex items-end gap-2">
                    <p className="text-3xl font-bold text-neutral-900">
                      {mockProgressData.thisWeek.completed}/{mockProgressData.thisWeek.total}
                    </p>
                    <p className="text-sm text-neutral-600 mb-1">{mockProgressData.thisWeek.percentage}%</p>
                  </div>
                  <div className="mt-3 h-2 bg-neutral-200 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-primary-600"
                      style={{ width: `${mockProgressData.thisWeek.percentage}%` }}
                    />
                  </div>
                </div>

                <div className="rounded-lg bg-neutral-50 p-4">
                  <p className="text-sm text-neutral-600 mb-2">This Month</p>
                  <div className="flex items-end gap-2">
                    <p className="text-3xl font-bold text-neutral-900">
                      {mockProgressData.thisMonth.completed}/{mockProgressData.thisMonth.total}
                    </p>
                    <p className="text-sm text-neutral-600 mb-1">{mockProgressData.thisMonth.percentage}%</p>
                  </div>
                  <div className="mt-3 h-2 bg-neutral-200 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-primary-600"
                      style={{ width: `${mockProgressData.thisMonth.percentage}%` }}
                    />
                  </div>
                </div>

                <div className="rounded-lg bg-neutral-50 p-4">
                  <p className="text-sm text-neutral-600 mb-2">All Time</p>
                  <div className="flex items-end gap-2">
                    <p className="text-3xl font-bold text-neutral-900">
                      {mockProgressData.allTime.completed}/{mockProgressData.allTime.total}
                    </p>
                    <p className="text-sm text-neutral-600 mb-1">{mockProgressData.allTime.percentage}%</p>
                  </div>
                  <div className="mt-3 h-2 bg-neutral-200 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-primary-600"
                      style={{ width: `${mockProgressData.allTime.percentage}%` }}
                    />
                  </div>
                </div>
              </div>

              {/* Mood Trend */}
              <div className="border-t border-neutral-200 pt-6">
                <h3 className="font-semibold text-neutral-900 mb-4">Mood Trend (Last 7 Days)</h3>
                <div className="flex items-end gap-2 h-32 bg-neutral-50 rounded-lg p-4">
                  {mockProgressData.moodTrend.map((mood, idx) => (
                    <div key={idx} className="flex-1 flex flex-col items-center">
                      <div
                        className="w-full rounded-t bg-gradient-to-t from-primary-600 to-primary-400"
                        style={{ height: `${(mood / 10) * 100}%`, minHeight: '4px' }}
                      />
                      <p className="text-xs text-neutral-600 mt-2">{['M', 'T', 'W', 'Th', 'F', 'Sa', 'Su'][idx]}</p>
                    </div>
                  ))}
                </div>
                <p className="text-sm text-neutral-600 mt-4">
                  Average mood: <span className="font-semibold text-neutral-900">{(mockProgressData.moodTrend.reduce((a, b) => a + b) / mockProgressData.moodTrend.length).toFixed(1)}/10</span>
                </p>
              </div>
            </Card>
          </div>
        )}

        {/* Footer */}
        <div className="mt-12 border-t border-neutral-200 pt-8">
          <Card className="bg-primary-50">
            <div className="text-center">
              <h3 className="text-xl font-semibold text-neutral-900 mb-2">Ready to Start Your Journey?</h3>
              <p className="text-neutral-600 mb-6">
                Join hundreds of users already experiencing lasting mental wellness with MHDF Momentum
              </p>
              <Button>Get Started Today</Button>
            </div>
          </Card>
        </div>
      </main>
    </>
  );
};

export default ClientPreview;
